// components/DataTable/DataTable.jsx

import React, {
  useCallback,
  useEffect,
  useState
} from "react";

import {
  Table,
  Alert,
  Button,
  Spinner
} from "react-bootstrap";

import DataTableCell from "./DataTableCell";
import DataTableActions from "./DataTableActions";
import DataTableEditForm from "./DataTableEditForm";
import DataTablePopoverEdit from "./DataTablePopoverEdit";

import { validateRow } from "./validation";
import { getRowKey } from "./utils";

import "./DataTable.css";

/**
 * @typedef {"string"|"number"|"bool"|"dropdown"} DataTableColumnType
 */

/**
 * @typedef {Object} DataTableColumn
 * @property {string} field
 * @property {string} title
 * @property {DataTableColumnType} type
 * @property {number} [maxLength]
 * @property {boolean} [required]
 * @property {string} [requiredMessage]
 * @property {function(*, Object): (string|null|undefined|boolean)} [validate]
 *
 * Dropdown:
 * @property {Array<Object>} [dataSource]
 * @property {string} [valueField]
 * @property {string} [textField]
 * @property {string} [placeholder]
 */

/**
 * @typedef {Object} DataTableAction
 * @property {string} title
 * @property {React.ReactNode} [icon]
 * @property {"edit"} [type]
 * @property {function(Object): void|Promise<void>} [onClick]
 * @property {function(Object): boolean} [visible]
 */

/**
 * @param {Object} props
 * @param {Array<Object>} props.data
 * @param {Array<DataTableColumn>} props.columns
 * @param {Array<DataTableAction>} [props.actions]
 * @param {string|function(Object): (string|number)} props.rowKey
 * @param {boolean} [props.editable=false]
 * @param {"row"|"popover"} [props.editMode="row"]
 * @param {string} [props.locale]
 * @param {boolean} [props.loading=false]
 * @param {string} [props.emptyMessage]
 * @param {function(Object,Object): void|Promise<void>} [props.onEditSave]
 */
function DataTable({
  data = [],
  columns = [],
  actions = [],
  rowKey,
  editable = false,
  editMode = "row",
  locale,
  loading = false,
  emptyMessage = "داده‌ای برای نمایش وجود ندارد",
  onEditSave
}) {
  const [editingKey, setEditingKey] =
    useState(null);

  const [editingRow, setEditingRow] =
    useState(null);

  const [originalRow, setOriginalRow] =
    useState(null);

  const [errors, setErrors] =
    useState({});

  const [saving, setSaving] =
    useState(false);

  const [saveError, setSaveError] =
    useState(null);

  const numberLocale =
    locale ||
    (typeof navigator !== "undefined"
      ? navigator.language
      : "en-US");

  const hasActions =
    Array.isArray(actions) &&
    actions.length > 0;

  /*
   * ----------------------------------------
   * Configuration validation
   * ----------------------------------------
   */

  useEffect(() => {
    if (!rowKey) {
      console.warn(
        "DataTable: rowKey is recommended and required for stable editable rows."
      );
    }

    const supportedTypes = [
      "string",
      "number",
      "bool",
      "dropdown"
    ];

    columns.forEach(column => {
      if (
        !supportedTypes.includes(
          column.type
        )
      ) {
        console.warn(
          `DataTable: unsupported type "${column.type}".`
        );
      }

      if (
        column.type === "dropdown"
      ) {
        if (
          !Array.isArray(
            column.dataSource
          )
        ) {
          console.warn(
            `DataTable: dropdown "${column.field}" requires dataSource.`
          );
        }

        if (!column.valueField) {
          console.warn(
            `DataTable: dropdown "${column.field}" requires valueField.`
          );
        }

        if (!column.textField) {
          console.warn(
            `DataTable: dropdown "${column.field}" requires textField.`
          );
        }
      }
    });
  }, [columns, rowKey]);

  /*
   * ----------------------------------------
   * Start Edit
   * ----------------------------------------
   */

  const startEdit = useCallback(
    (row, index) => {
      if (
        !editable ||
        loading ||
        saving
      ) {
        return;
      }

      const key = getRowKey(
        row,
        rowKey,
        index
      );

      setEditingKey(key);

      setOriginalRow({
        ...row
      });

      setEditingRow({
        ...row
      });

      setErrors({});
      setSaveError(null);
    },
    [
      editable,
      loading,
      saving,
      rowKey
    ]
  );

  /*
   * ----------------------------------------
   * Change
   * ----------------------------------------
   */

  const handleEditChange =
    useCallback(
      (field, value) => {
        setEditingRow(prev => {
          if (!prev) {
            return prev;
          }

          return {
            ...prev,
            [field]: value
          };
        });

        setErrors(prev => {
          if (!prev[field]) {
            return prev;
          }

          const next = {
            ...prev
          };

          delete next[field];

          return next;
        });

        setSaveError(null);
      },
      []
    );

  /*
   * ----------------------------------------
   * Cancel
   * ----------------------------------------
   */

  const cancelEdit = useCallback(() => {
    if (saving) {
      return;
    }

    setEditingKey(null);
    setEditingRow(null);
    setOriginalRow(null);
    setErrors({});
    setSaveError(null);
  }, [saving]);

  /*
   * ----------------------------------------
   * Save
   * ----------------------------------------
   */

  const saveEdit = useCallback(
    async () => {
      if (
        !editingRow ||
        !originalRow ||
        saving
      ) {
        return;
      }

      const validationErrors =
        validateRow(
          columns,
          editingRow
        );

      if (
        Object.keys(validationErrors)
          .length > 0
      ) {
        setErrors(validationErrors);
        return;
      }

      if (
        typeof onEditSave !== "function"
      ) {
        setSaveError(
          "onEditSave تعریف نشده است."
        );

        return;
      }

      setErrors({});
      setSaveError(null);
      setSaving(true);

      /*
       * Always pass copies.
       */
      const updatedRow = {
        ...editingRow
      };

      const oldRow = {
        ...originalRow
      };

      try {
        await Promise.resolve(
          onEditSave(
            updatedRow,
            oldRow
          )
        );

        /*
         * Success
         */
        setEditingKey(null);
        setEditingRow(null);
        setOriginalRow(null);
        setErrors({});
        setSaveError(null);
      } catch (error) {
        console.error(
          "DataTable onEditSave error:",
          error
        );

        setSaveError(
          error &&
            error.message
            ? error.message
            : "ذخیره اطلاعات با خطا مواجه شد."
        );

        /*
         * IMPORTANT:
         * Keep edit mode open.
         */
      } finally {
        setSaving(false);
      }
    },
    [
      editingRow,
      originalRow,
      saving,
      columns,
      onEditSave
    ]
  );

  /*
   * ----------------------------------------
   * Loading
   * ----------------------------------------
   */

  if (loading) {
    return (
      <div className="datatable-container">
        <div className="datatable-loading">
          <Spinner
            animation="border"
            role="status"
          />

          <span className="ml-2">
            در حال بارگذاری...
          </span>
        </div>
      </div>
    );
  }

  /*
   * ----------------------------------------
   * Empty
   * ----------------------------------------
   */

  if (
    !Array.isArray(data) ||
    data.length === 0
  ) {
    return (
      <div className="datatable-container">
        <div className="datatable-empty">
          {emptyMessage}
        </div>
      </div>
    );
  }

  /*
   * ----------------------------------------
   * Render
   * ----------------------------------------
   */

  return (
    <div className="datatable-container">
      {saveError && (
        <Alert
          variant="danger"
          className="mb-2"
        >
          {saveError}
        </Alert>
      )}

      <div className="table-responsive">
        <Table
          bordered
          hover
          className="datatable-table"
        >
          <thead>
            <tr>
              {columns.map(column => (
                <th
                  key={column.field}
                >
                  {column.title}
                </th>
              ))}

              {hasActions && (
                <th
                  className="datatable-actions-header"
                >
                  عملیات
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {data.map(
              (row, index) => {
                const key =
                  getRowKey(
                    row,
                    rowKey,
                    index
                  );

                const isEditing =
                  editingKey === key;

                const anotherRowEditing =
                  editingKey !== null &&
                  editingKey !== key;

                /*
                 * ----------------------------
                 * ROW EDIT
                 * ----------------------------
                 */

                if (
                  isEditing &&
                  editMode === "row"
                ) {
                  return (
                    <tr
                      key={String(key)}
                      className="datatable-editing-row"
                    >
                      {columns.map(
                        column => (
                          <td
                            key={
                              column.field
                            }
                          >
                            <DataTableEditForm
                              row={
                                editingRow
                              }
                              columns={[
                                column
                              ]}
                              errors={
                                errors
                              }
                              onChange={
                                handleEditChange
                              }
                              onSave={
                                saveEdit
                              }
                              onCancel={
                                cancelEdit
                              }
                              saving={
                                saving
                              }
                              showButtons={
                                false
                              }
                            />
                          </td>
                        )
                      )}

                      {hasActions && (
                        <td className="datatable-actions-cell">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={
                              saveEdit
                            }
                            disabled={
                              saving
                            }
                            aria-label="ذخیره"
                          >
                            {saving ? (
                              <Spinner
                                animation="border"
                                size="sm"
                              />
                            ) : (
                              "ذخیره"
                            )}
                          </Button>

                          <Button
                            variant="secondary"
                            size="sm"
                            className="ml-1"
                            onClick={
                              cancelEdit
                            }
                            disabled={
                              saving
                            }
                            aria-label="انصراف"
                          >
                            انصراف
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                }

                /*
                 * ----------------------------
                 * POPOVER EDIT
                 * ----------------------------
                 */

                if (
                  isEditing &&
                  editMode ===
                    "popover"
                ) {
                  /*
                   * We need the edit button
                   * itself as OverlayTrigger
                   * target.
                   *
                   * Therefore render normal cells
                   * and custom action area.
                   */

                  return (
                    <tr
                      key={String(key)}
                    >
                      {columns.map(
                        column => (
                          <td
                            key={
                              column.field
                            }
                          >
                            <DataTableCell
                              row={row}
                              column={
                                column
                              }
                              locale={
                                numberLocale
                              }
                            />
                          </td>
                        )
                      )}

                      {hasActions && (
                        <td className="datatable-actions-cell">
                          <DataTableActions
                            row={row}
                            actions={
                              actions
                            }
                            onEdit={
                              () =>
                                startEdit(
                                  row,
                                  index
                                )
                            }
                            editable={
                              editable
                            }
                            disabled={
                              anotherRowEditing ||
                              saving
                            }
                          />

                          <div className="datatable-popover-host">
                            <DataTableEditForm
                              row={
                                editingRow
                              }
                              columns={
                                columns
                              }
                              errors={
                                errors
                              }
                              onChange={
                                handleEditChange
                              }
                              onSave={
                                saveEdit
                              }
                              onCancel={
                                cancelEdit
                              }
                              saving={
                                saving
                              }
                              showButtons={
                                true
                              }
                            />
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                }

                /*
                 * ----------------------------
                 * VIEW MODE
                 * ----------------------------
                 */

                return (
                  <tr
                    key={String(key)}
                  >
                    {columns.map(
                      column => (
                        <td
                          key={
                            column.field
                          }
                        >
                          <DataTableCell
                            row={row}
                            column={
                              column
                            }
                            locale={
                              numberLocale
                            }
                          />
                        </td>
                      )
                    )}

                    {hasActions && (
                      <td className="datatable-actions-cell">
                        <DataTableActions
                          row={row}
                          actions={
                            actions
                          }
                          onEdit={
                            () =>
                              startEdit(
                                row,
                                index
                              )
                          }
                          editable={
                            editable
                          }
                          disabled={
                            anotherRowEditing ||
                            saving
                          }
                        />
                      </td>
                    )}
                  </tr>
                );
              }
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

export default DataTable;